/* eslint-disable  @typescript-eslint/no-explicit-any */
import * as React from "react";
import { useRef, useState } from "react";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import { AlertColor, Button, CircularProgress, DialogContentText } from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import { Notify } from "./Notify";
import { InstanceTypePicker, InstanceType } from "./InstanceTypePicker";
import DOMPurify from "dompurify";

export interface InstanceTypePickerDialogProps {
  visible: boolean;
  dialogTitle: string;
  actionButtonLabel: string;
  dialogMessage?: string;
  onCancel: () => void;
  onAction: (instanceType: InstanceType) => Promise<void>;
}

export const InstanceTypePickerDialog = ({
  visible,
  dialogTitle,
  actionButtonLabel,
  dialogMessage,
  onCancel,
  onAction,
}: InstanceTypePickerDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("info");
  const refInstanceType = useRef<any>(null);
  const [instanceTypeError, setInstanceTypeError] = useState<string | undefined>(undefined);
  const [selectedInstanceType, setSelectedInstanceType] = useState<InstanceType | undefined>(undefined);

  const handleInstanceTypeSelected = (instanceType: InstanceType) => {
    setSelectedInstanceType(instanceType);
    setInstanceTypeError(undefined);
  };

  const actionButtonClickHandler = async () => {
    if (selectedInstanceType) {
      setLoading(true);
      try {
        await onAction(selectedInstanceType);
      } catch (e: any) {
        setAlertMessage(e.debugMessage || "Failed to perform action with the selected instance type.");
        setAlertSeverity("error");
      } finally {
        setLoading(false);
      }
    } else if (!refInstanceType.current?.value) {
      setInstanceTypeError("You must select an Instance Type");
    }
  };

  return (
    <>
      <Dialog open={visible}>
        <DialogTitle>{dialogTitle}</DialogTitle>
        {dialogMessage !== undefined && (
          <DialogContent>
            <DialogContentText
              id="alert-dialog-message"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(dialogMessage) }}
            />
          </DialogContent>
        )}
        <DialogContent>
          <Box component="form" sx={{ display: "flex", flexWrap: "wrap" }}>
            {loading ? (
              <CircularProgress />
            ) : (
              <InstanceTypePicker
                selectRef={refInstanceType}
                error={instanceTypeError}
                onInstanceTypeSelected={handleInstanceTypeSelected}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onCancel} color="error" disabled={loading}>
            Cancel
          </Button>
          <Button variant="outlined" onClick={actionButtonClickHandler} disabled={loading}>
            {actionButtonLabel}
          </Button>
        </DialogActions>
      </Dialog>
      {alertMessage && <Notify severity={alertSeverity} message={alertMessage} />}
    </>
  );
};
